import { Group, Loader, Stack, Text, ThemeIcon } from '@mantine/core';
import { BsClock, BsExclamationTriangle } from 'react-icons/bs';
import { DEFAULT_PREVIEW_LENGTH } from './config';
import { NodeId, NodeEvaluationStatus } from '../../types';
import { match } from 'ts-pattern';
import { useGraphContext } from '../../context';
import { OutputValueDisplay } from '../output-value';
import { OutputCard } from './styles';
import { useStore } from '@/store';

interface GraphNodeOutputProps {
  id: NodeId;
}

export const GraphNodeOutput = ({ id }: GraphNodeOutputProps) => {
  const graphId = useGraphContext(({ eval: ev }) => ev.getGraphId());
  const IEval = useStore(({ graphs: g }) => g.evaluation(graphId));
  const error = IEval.getNodeError(id);

  return (
    <OutputCard radius="sm" p="xs" withBorder>
      {match(IEval.getNodeStatus(id))
        .with(NodeEvaluationStatus.Idle, () => (
          <Group spacing="xs" align="center">
            <ThemeIcon color="gray" variant="light" size="sm" radius="md">
              <BsClock />
            </ThemeIcon>
            <Stack spacing={2}>
              <Text fz="xs" fw={600}>
                Not run yet
              </Text>
              <Text fz="xs" c="dimmed">
                Execute to preview output
              </Text>
            </Stack>
          </Group>
        ))
        .with(NodeEvaluationStatus.Running, () => (
          <Group spacing="xs" align="center">
            <Loader size="xs" />
            <Stack spacing={2}>
              <Text fz="xs" fw={600}>
                Evaluating
              </Text>
              <Text fz="xs" c="dimmed">
                Computing intermediate output
              </Text>
            </Stack>
          </Group>
        ))
        .with(NodeEvaluationStatus.Error, () => (
          <Group spacing="xs" align="center" noWrap>
            <ThemeIcon
              color="red"
              variant="outline"
              size="sm"
              radius="md"
              sx={{ border: 'none' }}
            >
              <BsExclamationTriangle />
            </ThemeIcon>
            <Stack spacing={2}>
              <Text fz="xs" fw={600}>
                Failed to evaluate
              </Text>
              {error && (
                <Text fz="xs" c="dimmed" lineClamp={2}>
                  {error.message}
                </Text>
              )}
            </Stack>
          </Group>
        ))
        .with(NodeEvaluationStatus.Ok, () => (
          <Stack spacing={6}>
            <OutputValueDisplay
              value={IEval.getNodeValue(id)}
              maxLength={DEFAULT_PREVIEW_LENGTH}
            />
          </Stack>
        ))
        .exhaustive()}
    </OutputCard>
  );
};
